import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useStudyStore } from '../store/useStudyStore'
import { GameResultService } from '../services/game/GameResultService'
import { GameAnswer } from '../types/database'
import { 
  Database, 
  Cloud, 
  Download, 
  RefreshCw, 
  CheckCircle, 
  XCircle,
  Trophy,
  Clock,
  Target,
  Upload
} from 'lucide-react'
import toast from 'react-hot-toast'
import { normalizeVatliUrl } from '../utils/urlNormalizer'

/**
 * Trang test Database - Kiểm tra lưu kết quả game
 */
const TestGameDatabase: React.FC = () => {
  const { 
    gameResults, 
    submitGame, 
    loadGameResultsFromServer,
    syncGameResultsToServer,
    currentUser 
  } = useStudyStore()
  
  const [isTestingLocal, setIsTestingLocal] = useState(false)
  const [isTestingWordPress, setIsTestingWordPress] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isSyncing, setIsSyncing] = useState(false)
  const [testResults, setTestResults] = useState<{
    local: boolean | null
    wordpress: boolean | null
  }>({
    local: null,
    wordpress: null
  })
  const defaultApiUrl = import.meta.env.DEV
    ? 'http://localhost/wordpress/wp-json/qoder/v1'
    : 'https://vatli365.vn/wp-json/qoder/v1'
  const resolvedApiUrl = normalizeVatliUrl(import.meta.env.VITE_WP_API_URL, defaultApiUrl)

  // Test Local Storage
  const testLocalStorage = async () => {
    setIsTestingLocal(true)
    
    try {
      // Tạo game result test
      const mockAnswers: GameAnswer[] = [
        {
          questionId: 'test-q1',
          questionText: 'Test question 1?',
          userAnswer: 'A',
          correctAnswer: 'A',
          isCorrect: true,
          timeSpent: 10,
          roomNumber: 1
        },
        {
          questionId: 'test-q2',
          questionText: 'Test question 2?',
          userAnswer: 'B',
          correctAnswer: 'B',
          isCorrect: true,
          timeSpent: 12,
          roomNumber: 2
        }
      ]

      await submitGame(
        'test-game-local',
        'test-lesson',
        '11',
        '1',
        2,
        2,
        22,
        mockAnswers,
        true,
        2
      )

      setTestResults(prev => ({ ...prev, local: true }))
      toast.success('✅ Lưu local thành công!')
      
    } catch (error) {
      console.error('Local test failed:', error)
      setTestResults(prev => ({ ...prev, local: false }))
      toast.error('❌ Lưu local thất bại!')
    } finally {
      setIsTestingLocal(false)
    }
  }

  // Test WordPress API
  const testWordPressAPI = async () => {
    setIsTestingWordPress(true)
    
    try {
      const mockAnswers: GameAnswer[] = [
        {
          questionId: 'test-wp-q1',
          questionText: 'WordPress test question 1?',
          userAnswer: 'C',
          correctAnswer: 'C',
          isCorrect: true,
          timeSpent: 8,
          roomNumber: 1
        },
        {
          questionId: 'test-wp-q2',
          questionText: 'WordPress test question 2?',
          userAnswer: 'D',
          correctAnswer: 'D',
          isCorrect: true,
          timeSpent: 15,
          roomNumber: 2
        },
        {
          questionId: 'test-wp-q3',
          questionText: 'WordPress test question 3?',
          userAnswer: 'A',
          correctAnswer: 'A',
          isCorrect: true,
          timeSpent: 11,
          roomNumber: 3
        }
      ]

      const testResult = {
        id: `test_wp_${Date.now()}`,
        userId: currentUser?.id || 'test_user',
        gameId: 'test-game-wp',
        lessonId: 'test-lesson-wp',
        classId: '11',
        chapterId: '1',
        score: 3,
        maxScore: 3,
        percentage: 100,
        timeSpent: 34,
        answers: mockAnswers,
        startedAt: new Date(Date.now() - 34000),
        completedAt: new Date(),
        won: true,
        attempts: 1,
        roomsCompleted: 3
      }

      await GameResultService.saveGameResult(testResult)
      
      setTestResults(prev => ({ ...prev, wordpress: true }))
      toast.success('✅ Lưu WordPress thành công!')
      
    } catch (error) {
      console.error('WordPress test failed:', error)
      setTestResults(prev => ({ ...prev, wordpress: false }))
      toast.error('❌ Lưu WordPress thất bại! Kiểm tra API URL')
    } finally {
      setIsTestingWordPress(false)
    }
  }

  // Load from server
  const loadFromServer = async () => {
    setIsLoading(true)
    
    try {
      const userId = currentUser?.id || 'test_user'
      await loadGameResultsFromServer(userId)
      toast.success('✅ Tải dữ liệu từ server thành công!')
    } catch (error) {
      toast.error('❌ Không thể tải dữ liệu từ server')
    } finally {
      setIsLoading(false)
    }
  }

  // Sync to server
  const syncToServer = async () => {
    setIsSyncing(true)
    
    try {
      const result = await syncGameResultsToServer()
      toast.success(`✅ Đồng bộ: ${result.synced} thành công, ${result.failed} thất bại`)
    } catch (error) {
      toast.error('❌ Không thể đồng bộ dữ liệu')
    } finally {
      setIsSyncing(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3 mb-2">
            <Database className="w-8 h-8 text-blue-600" />
            Test Game Database
          </h1>
          <p className="text-gray-600">
            Kiểm tra tính năng lưu kết quả game - Local Storage và WordPress API
          </p>
        </motion.div>

        {/* Test Controls */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Local Storage Test */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Database className="w-6 h-6 text-green-600" />
              Local Storage
            </h2>
            
            <p className="text-sm text-gray-600 mb-4">
              Test lưu kết quả vào localStorage (Zustand Persist)
            </p>

            <button
              onClick={testLocalStorage}
              disabled={isTestingLocal}
              className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-green-600 hover:to-emerald-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isTestingLocal ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  Đang test...
                </>
              ) : (
                <>
                  <Database className="w-5 h-5" />
                  Test Local Storage
                </>
              )}
            </button>

            {testResults.local !== null && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className={`mt-4 p-3 rounded-lg flex items-center gap-2 ${
                  testResults.local 
                    ? 'bg-green-50 text-green-700 border border-green-200' 
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}
              >
                {testResults.local ? (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">Lưu local thành công!</span>
                  </>
                ) : (
                  <>
                    <XCircle className="w-5 h-5" />
                    <span className="font-medium">Lưu local thất bại!</span>
                  </>
                )}
              </motion.div>
            )}
          </motion.div>

          {/* WordPress API Test */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Cloud className="w-6 h-6 text-blue-600" />
            WordPress API
          </h2>
            
            <p className="text-sm text-gray-600 mb-4">
              Test lưu kết quả lên WordPress backend
            </p>

            <button
              onClick={testWordPressAPI}
              disabled={isTestingWordPress}
              className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-600 hover:to-indigo-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isTestingWordPress ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  Đang test...
                </>
              ) : (
                <>
                  <Cloud className="w-5 h-5" />
                  Test WordPress API
                </>
              )}
            </button>

            {testResults.wordpress !== null && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className={`mt-4 p-3 rounded-lg flex items-center gap-2 ${
                  testResults.wordpress 
                    ? 'bg-green-50 text-green-700 border border-green-200' 
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}
              >
                {testResults.wordpress ? (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">Lưu WordPress thành công!</span>
                  </>
                ) : (
                  <>
                    <XCircle className="w-5 h-5" />
                    <span className="font-medium">Lưu WordPress thất bại!</span>
                  </>
                )}
              </motion.div>
            )}
          </motion.div>
        </div>

        {/* Sync Controls */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <h2 className="text-xl font-bold text-gray-900 mb-4">Đồng bộ dữ liệu</h2>
          
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={loadFromServer}
              disabled={isLoading}
              className="bg-blue-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <RefreshCw className="w-5 h-5 animate-spin" />
              ) : (
                <Download className="w-5 h-5" />
              )}
              Tải từ Server
            </button>

            <button
              onClick={syncToServer}
              disabled={isSyncing}
              className="bg-purple-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isSyncing ? (
                <RefreshCw className="w-5 h-5 animate-spin" />
              ) : (
                <Upload className="w-5 h-5" />
              )}
              Đồng bộ lên Server
            </button>
          </div>
        </motion.div>

        {/* Results Display */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Trophy className="w-6 h-6 text-yellow-500" />
            Kết quả trong Local Storage ({gameResults.length})
          </h2>

          {gameResults.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Database className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Chưa có kết quả game</p>
              <p className="text-sm">Chạy test để tạo dữ liệu mẫu</p>
            </div>
          ) : (
            <div className="space-y-3">
              {gameResults.slice(0, 10).map((result, index) => (
                <motion.div
                  key={result.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`p-4 rounded-lg border ${
                    result.won 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-900 mb-1">
                        {result.won ? '🎉 Thắng' : '❌ Thua'} - {result.gameId}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <Target className="w-4 h-4" />
                          {result.score}/{result.maxScore} ({result.percentage.toFixed(0)}%)
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {Math.floor(result.timeSpent / 60)}:{(result.timeSpent % 60).toString().padStart(2, '0')}
                        </div>
                        <div className="text-xs">
                          Lần thử #{result.attempts}
                        </div>
                      </div>
                    </div>
                    <div className="text-2xl font-bold text-gray-900">
                      {result.percentage.toFixed(0)}%
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>

        {/* API Info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="bg-blue-50 border border-blue-200 rounded-xl p-6"
        >
          <h3 className="font-bold text-blue-900 mb-3">ℹ️ Cấu hình API</h3>
          <div className="space-y-2 text-sm text-blue-800">
            <p>
              <strong>API Base URL:</strong> {resolvedApiUrl}
            </p>
            <p>
              <strong>Current User:</strong> {currentUser?.id || 'anonymous'}
            </p>
            <p>
              <strong>Total Results:</strong> {gameResults.length}
            </p>
            <div className="mt-4 p-3 bg-blue-100 rounded-lg">
              <p className="font-medium mb-2">📝 Để kết nối WordPress:</p>
              <ol className="list-decimal list-inside space-y-1 text-xs">
                <li>Tạo file <code>.env</code> với <code>VITE_WP_API_URL</code></li>
                <li>Upload plugin <code>qoder-user-api</code> lên WordPress</li>
                <li>Activate plugin trong WordPress Admin</li>
                <li>Test API bằng nút "Test WordPress API"</li>
              </ol>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default TestGameDatabase

